import React, { useState, useEffect, useRef, useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTags,
  faPlus,
  faSearch,
  faTimes,
  faTag
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

import TagCard from './tags/TagCard';
import TagDetailModal from './tags/TagDetailModal';
import TagCompleteModal from './tags/TagCompleteModal';
import TagDeleteModal from './tags/TagDeleteModal';
import TagPhotoModals from './tags/TagPhotoModals';

const TagsSettingsSection = () => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token, nombreCompleto } = useToken();
  const { showSuccessToast, showErrorToast } = useToast();

  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const isInitialMount = useRef(true);

  // Modal Detalle / Vista / Edición State
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formStatus, setFormStatus] = useState(1);
  const [isSaving, setIsSaving] = useState(false);

  // Modal Eliminar Incidencia State
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Modal Completar Incidencia State
  const [itemToComplete, setItemToComplete] = useState(null);
  const [isCompleting, setIsCompleting] = useState(false);

  // Tickets vinculados a la incidencia seleccionada
  const [tagTickets, setTagTickets] = useState([]);
  const [isLoadingTagTickets, setIsLoadingTagTickets] = useState(false);

  // Fotos / Evidencias State (Integrado en Vista de Incidencia)
  const [photosList, setPhotosList] = useState([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoDescription, setPhotoDescription] = useState('');
  const [selectedPhotoPreview, setSelectedPhotoPreview] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Cargar catálogo de etiquetas desde la API
  const fetchTags = async (searchQuery = searchTerm) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery && searchQuery.trim()) params.append('searchTerm', searchQuery.trim());

      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`/api/SupportEngine/GetEtiquetas${query}`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        }
      });

      if (response.ok) {
        const result = await response.json();
        const list = result.data || result;
        if (Array.isArray(list)) {
          setTags(list);
        }
      } else {
        showErrorToast('Error al cargar la lista de incidencias.');
      }
    } catch (err) {
      console.error('Error al cargar etiquetas:', err);
      showErrorToast('Error de conexión al cargar incidencias.');
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar datos al montar y con debounce al buscar
  useEffect(() => {
    if (!token) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchTags(searchTerm);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(() => {
      fetchTags(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, token]);

  // Abrir modal para crear
  const handleOpenCreate = () => {
    setSelectedTag(null);
    setFormName('');
    setFormDescription('');
    setFormStatus(1);
    setIsEditMode(true);
    setIsUploadOpen(false);
    setSelectedPhotoPreview(null);
    setPhotoDescription('');
    setPhotosList([]);
    setTagTickets([]);
    setIsDetailModalOpen(true);
  };

  // Abrir vista/detalle de incidencia
  const handleOpenView = async tag => {
    setSelectedTag(tag);
    setFormName(tag.vNombre || '');
    setFormDescription(tag.vDescripcion || '');
    setFormStatus(Number(tag.iEstadoIncidencia) || 1);
    setIsEditMode(false);
    setIsUploadOpen(false);
    setSelectedPhotoPreview(null);
    setPhotoDescription('');
    setIsDetailModalOpen(true);
    await Promise.all([loadPhotos(tag.idEtiqueta), loadTagTickets(tag.idEtiqueta)]);
  };

  // Abrir modal en modo edición
  const handleOpenEdit = async tag => {
    setSelectedTag(tag);
    setFormName(tag.vNombre || '');
    setFormDescription(tag.vDescripcion || '');
    setFormStatus(Number(tag.iEstadoIncidencia) || 1);
    setIsEditMode(true);
    setIsUploadOpen(false);
    setSelectedPhotoPreview(null);
    setPhotoDescription('');
    setIsDetailModalOpen(true);
    await Promise.all([loadPhotos(tag.idEtiqueta), loadTagTickets(tag.idEtiqueta)]);
  };

  // Guardar (Crear o Actualizar)
  const handleSave = async e => {
    e.preventDefault();
    const cleanName = formName.trim();
    const cleanDesc = formDescription.trim();

    if (!cleanName) {
      showErrorToast('El nombre de la incidencia es obligatorio.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        idEtiqueta: selectedTag ? selectedTag.idEtiqueta : null,
        vNombre: cleanName,
        vDescripcion: cleanDesc || null,
        iEstadoIncidencia: Number(formStatus) || 1,
        vCreatedUser: nombreCompleto || 'Sistema'
      };

      const response = await fetch('/api/SupportEngine/SaveEtiqueta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        let msg = result.message || 'Error al guardar la incidencia';
        if (result.details) {
          try {
            const parsed = typeof result.details === 'string' ? JSON.parse(result.details) : result.details;
            if (parsed.message) msg = parsed.message;
          } catch (_) {
            msg = result.details;
          }
        }
        showErrorToast(msg);
        return;
      }

      // Identificar y subir fotos pendientes si existen
      const pendingPhotos = [
        ...photosList.filter(p => p.isPendingUpload || String(p.idIncidenciaFoto).startsWith('temp_'))
      ];

      if (selectedPhotoPreview) {
        pendingPhotos.push({
          vNombreArchivo: selectedPhotoPreview.fileName,
          vUrlFoto: selectedPhotoPreview.dataUrl,
          vDescripcion: photoDescription.trim() || null
        });
      }

      let tagIdToUse = selectedTag?.idEtiqueta;

      if (!tagIdToUse && pendingPhotos.length > 0) {
        try {
          const tagsResp = await fetch(`/api/SupportEngine/GetEtiquetas?searchTerm=${encodeURIComponent(cleanName)}`, {
            headers: {
              'Accept': 'application/json',
              'Authorization': 'Bearer ' + token
            }
          });
          if (tagsResp.ok) {
            const tagsResult = await tagsResp.json().catch(() => ({}));
            const list = tagsResult.data || tagsResult;
            if (Array.isArray(list)) {
              const foundTag = list.find(t => t.vNombre.trim().toLowerCase() === cleanName.toLowerCase());
              if (foundTag) tagIdToUse = foundTag.idEtiqueta;
            }
          }
        } catch (errSearch) {
          console.error('Error al obtener id de la nueva incidencia:', errSearch);
        }
      }

      if (tagIdToUse && pendingPhotos.length > 0) {
        for (const p of pendingPhotos) {
          try {
            await fetch('/api/SupportEngine/SaveIncidenciaFoto', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: 'Bearer ' + token
              },
              body: JSON.stringify({
                idEtiqueta: tagIdToUse,
                vNombreArchivo: p.vNombreArchivo,
                vUrlFoto: p.vUrlFoto,
                vDescripcion: p.vDescripcion,
                vCreatedUser: nombreCompleto || 'Sistema'
              })
            });
          } catch (errPhoto) {
            console.error('Error al subir foto pendiente:', errPhoto);
          }
        }
      }

      setSelectedPhotoPreview(null);
      setPhotoDescription('');

      if (selectedTag) {
        showSuccessToast('Incidencia actualizada exitosamente.');
        setSelectedTag(prev => (prev ? {
          ...prev,
          vNombre: cleanName,
          vDescripcion: cleanDesc,
          iEstadoIncidencia: Number(formStatus)
        } : null));
        setIsEditMode(false);
      } else {
        showSuccessToast('Incidencia creada exitosamente.');
        setIsDetailModalOpen(false);
      }
      fetchTags(searchTerm);
    } catch (err) {
      console.error('Error al guardar incidencia:', err);
      showErrorToast('Error de conexión al guardar la incidencia.');
    } finally {
      setIsSaving(false);
    }
  };

  // Abrir confirmación de eliminación
  const handleOpenDelete = tag => {
    setItemToDelete(tag);
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);

    try {
      const payload = {
        idEtiqueta: itemToDelete.idEtiqueta,
        vUpdatedUser: nombreCompleto || 'Sistema'
      };

      const response = await fetch('/api/SupportEngine/DeleteEtiqueta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        showErrorToast('No se pudo desactivar la incidencia.');
        return;
      }

      showSuccessToast('Incidencia eliminada exitosamente.');
      setItemToDelete(null);
      fetchTags(searchTerm);
    } catch (err) {
      console.error('Error al eliminar etiqueta:', err);
      showErrorToast('Error de conexión al eliminar la incidencia.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Abrir modal de completar/resolver incidencia
  const handleOpenComplete = tag => {
    if (!tag || Number(tag.iTotalTickets) <= 0) {
      showErrorToast('No se puede resolver: la incidencia no tiene tickets vinculados.');
      return;
    }
    setItemToComplete(tag);
  };

  // Confirmar resolver incidencia
  const handleConfirmComplete = async (customMessage) => {
    if (!itemToComplete || Number(itemToComplete.iTotalTickets) <= 0) return;
    setIsCompleting(true);

    try {
      const payload = {
        idEtiqueta: itemToComplete.idEtiqueta,
        vUpdatedUser: nombreCompleto || 'Sistema',
        vMensajeResolucion: customMessage || null
      };

      const response = await fetch('/api/SupportEngine/CompleteEtiqueta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok && (result.success || result.iResponseType === 1)) {
        showSuccessToast('Incidencia resuelta exitosamente.');
        setItemToComplete(null);
        if (selectedTag && selectedTag.idEtiqueta === itemToComplete.idEtiqueta) {
          setSelectedTag(prev => (prev ? { ...prev, iEstadoIncidencia: 3 } : null));
          loadTagTickets(itemToComplete.idEtiqueta);
        }
        fetchTags(searchTerm);
      } else {
        showErrorToast(result.message || result.vErrorDescription || 'No se pudo resolver la incidencia.');
      }
    } catch (err) {
      console.error('Error al resolver incidencia:', err);
      showErrorToast('Error de conexión al resolver la incidencia.');
    } finally {
      setIsCompleting(false);
    }
  };

  // Cargar fotos para una incidencia
  const loadPhotos = async tagId => {
    if (!tagId) {
      setPhotosList([]);
      return;
    }
    setIsLoadingPhotos(true);
    try {
      const response = await fetch(`/api/SupportEngine/GetIncidenciaFotos?idEtiqueta=${tagId}`, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });
      if (response.ok) {
        const result = await response.json();
        const list = result.data || result;
        setPhotosList(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error('Error al cargar fotos:', err);
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  // Cargar tickets vinculados a una incidencia
  const loadTagTickets = async tagId => {
    if (!tagId) {
      setTagTickets([]);
      return;
    }
    setIsLoadingTagTickets(true);
    try {
      const response = await fetch(`/api/SupportEngine/GetTagTickets?idEtiqueta=${tagId}`, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });
      if (response.ok) {
        const result = await response.json();
        const list = result.data || result;
        setTagTickets(Array.isArray(list) ? list : []);
      } else {
        setTagTickets([]);
      }
    } catch (err) {
      console.error('Error al cargar tickets vinculados:', err);
      setTagTickets([]);
    } finally {
      setIsLoadingTagTickets(false);
    }
  };

  // Seleccionar archivo para previsualización (sin subir todavía)
  const handleFileSelect = e => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showErrorToast('Por favor selecciona un archivo de imagen válido.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showErrorToast('La imagen no debe superar los 10 MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = event => {
      const base64Url = event.target?.result;
      if (!base64Url) {
        showErrorToast('Error al procesar la imagen.');
        return;
      }

      setSelectedPhotoPreview({
        file,
        dataUrl: base64Url,
        fileName: file.name,
        fileSize: file.size
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
    };

    reader.onerror = () => {
      showErrorToast('Error al leer el archivo.');
    };

    reader.readAsDataURL(file);
  };

  // Descartar vista previa de foto
  const handleDiscardPhotoPreview = () => {
    setSelectedPhotoPreview(null);
    setPhotoDescription('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Guardar/subir foto tras confirmar la vista previa
  const handleSavePhotoPreview = async () => {
    if (!selectedPhotoPreview) return;

    // Si estamos creando una nueva incidencia, guardamos la foto localmente en la lista de evidencias
    if (!selectedTag) {
      const tempPhoto = {
        idIncidenciaFoto: 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
        vNombreArchivo: selectedPhotoPreview.fileName,
        vUrlFoto: selectedPhotoPreview.dataUrl,
        vDescripcion: photoDescription.trim() || null,
        dCreatedDate: new Date().toISOString(),
        vCreatedUser: nombreCompleto || 'Sistema',
        isPendingUpload: true
      };

      setPhotosList(prev => [...prev, tempPhoto]);
      setSelectedPhotoPreview(null);
      setPhotoDescription('');
      setIsUploadOpen(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      showSuccessToast('Foto agregada a la lista de evidencias.');
      return;
    }

    setIsUploadingPhoto(true);

    try {
      const payload = {
        idEtiqueta: selectedTag.idEtiqueta,
        vNombreArchivo: selectedPhotoPreview.fileName,
        vUrlFoto: selectedPhotoPreview.dataUrl,
        vDescripcion: photoDescription.trim() || null,
        vCreatedUser: nombreCompleto || 'Sistema'
      };

      const response = await fetch('/api/SupportEngine/SaveIncidenciaFoto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showSuccessToast('Foto agregada exitosamente.');
        setSelectedPhotoPreview(null);
        setPhotoDescription('');
        setIsUploadOpen(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        await loadPhotos(selectedTag.idEtiqueta);
        fetchTags(searchTerm);
      } else {
        showErrorToast('Error al guardar la foto.');
      }
    } catch (err) {
      console.error('Error al subir foto:', err);
      showErrorToast('Error de conexión al subir la foto.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // Confirmar eliminación de foto
  const handleConfirmDeletePhoto = async () => {
    if (!photoToDelete) return;

    // Si es una foto pendiente (local), eliminarla de la lista en memoria
    if (photoToDelete.isPendingUpload || String(photoToDelete.idIncidenciaFoto).startsWith('temp_')) {
      setPhotosList(prev => prev.filter(p => p.idIncidenciaFoto !== photoToDelete.idIncidenciaFoto));
      setPhotoToDelete(null);
      showSuccessToast('Foto eliminada.');
      return;
    }

    if (!selectedTag) return;
    setIsDeletingPhoto(true);
    try {
      const payload = {
        idIncidenciaFoto: photoToDelete.idIncidenciaFoto,
        vUpdatedUser: nombreCompleto || 'Sistema'
      };

      const response = await fetch('/api/SupportEngine/DeleteIncidenciaFoto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showSuccessToast('Foto eliminada exitosamente.');
        setPhotoToDelete(null);
        await loadPhotos(selectedTag.idEtiqueta);
        fetchTags(searchTerm);
      } else {
        showErrorToast('No se pudo eliminar la foto.');
      }
    } catch (err) {
      console.error('Error al eliminar foto:', err);
      showErrorToast('Error de conexión al eliminar la foto.');
    } finally {
      setIsDeletingPhoto(false);
    }
  };

  return (
    <div
      className="card border rounded-3 shadow-none mb-4"
      style={{
        backgroundColor: isDark ? '#101c26' : '#ffffff',
        borderColor: isDark ? '#233142' : '#e2e8f0'
      }}
    >
      {/* Header de la Sección */}
      <div className="card-header bg-transparent border-0 pb-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h5 className="mb-1 d-flex align-items-center gap-2 fw-bold" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
            <div
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: isDark ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                color: isDark ? '#60a5fa' : '#2563eb'
              }}
            >
              <FontAwesomeIcon icon={faTags} style={{ fontSize: '0.9rem' }} />
            </div>
            <span>Catálogo de Incidencias (Etiquetas)</span>
          </h5>
          <p className="small mb-0" style={{ fontSize: '0.84rem', color: isDark ? '#94a3b8' : '#64748b' }}>
            Crea y administra etiquetas para clasificar los problemas reportados en los tickets de soporte.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2 ms-auto">
          <button
            type="button"
            className="btn btn-sm btn-primary rounded-pill px-3 shadow-none d-flex align-items-center gap-2 fw-semibold"
            style={{ backgroundColor: '#2563eb', borderColor: '#2563eb' }}
            onClick={handleOpenCreate}
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" style={{ fontSize: '0.78rem' }} />
            <span>Nueva Incidencia</span>
          </button>
        </div>
      </div>

      {/* Barra de Búsqueda */}
      <div
        className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-2 border-top"
        style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
      >
        <div
          className="d-flex align-items-center gap-2 px-3 py-1 border rounded-pill"
          style={{
            width: '370px',
            maxWidth: '100%',
            backgroundColor: isDark ? '#182635' : '#f8fafc',
            borderColor: isDark ? '#233142' : '#e2e8f0'
          }}
        >
          <FontAwesomeIcon icon={faSearch} className="text-muted" style={{ fontSize: '0.82rem' }} />
          <input
            type="text"
            className="border-0 bg-transparent shadow-none w-100"
            style={{ fontSize: '0.84rem', outline: 'none', color: isDark ? '#f9fafd' : '#0f172a' }}
            placeholder="Buscar incidencia por nombre o descripción..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className="btn btn-sm p-0 text-muted border-0 bg-transparent"
              onClick={() => setSearchTerm('')}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.75rem' }} />
            </button>
          )}
        </div>

        <div className="text-muted small fw-medium" style={{ fontSize: '0.82rem' }}>
          Total: <span className="fw-bold" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{tags.length}</span> incidencias activas
        </div>
      </div>

      {/* Contenido / Listado de Etiquetas */}
      <div className="card-body p-3 pt-2">
        {isLoading ? (
          <div className="py-5 text-center text-muted">
            <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
            <div className="small">Cargando incidencias...</div>
          </div>
        ) : tags.length === 0 ? (
          <div
            className="text-center py-5 rounded-3 border border-dashed"
            style={{ borderColor: isDark ? '#233142' : '#cbd5e1' }}
          >
            <FontAwesomeIcon icon={faTag} className="text-muted mb-2" style={{ fontSize: '2rem', opacity: 0.4 }} />
            <h6 className="fw-semibold mb-1" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
              No se encontraron incidencias
            </h6>
            <p className="small text-muted mb-3" style={{ maxWidth: '400px', margin: '0 auto' }}>
              {searchTerm
                ? 'No hay resultados que coincidan con la búsqueda.'
                : 'Aún no has registrado ninguna etiqueta. Crea una para clasificar los problemas de los clientes.'}
            </p>
            {!searchTerm && (
              <button
                type="button"
                className="btn btn-sm btn-primary rounded-pill px-3 fw-semibold shadow-none"
                onClick={handleOpenCreate}
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" /> Crear Primera Incidencia
              </button>
            )}
          </div>
        ) : (
          <div className="row g-3">
            {tags.map(tag => (
              <TagCard
                key={tag.idEtiqueta}
                tag={tag}
                isDark={isDark}
                onOpenView={handleOpenView}
                onOpenEdit={handleOpenEdit}
                onOpenComplete={handleOpenComplete}
                onOpenDelete={handleOpenDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* MODAL DETALLE / VISTA Y EDICIÓN (UNIFICADO) */}
      <TagDetailModal
        isOpen={isDetailModalOpen}
        selectedTag={selectedTag}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        formName={formName}
        setFormName={setFormName}
        formDescription={formDescription}
        setFormDescription={setFormDescription}
        formStatus={formStatus}
        setFormStatus={setFormStatus}
        isSaving={isSaving}
        onSave={handleSave}
        onClose={() => {
          setSelectedPhotoPreview(null);
          setPhotoDescription('');
          setIsUploadOpen(false);
          setIsDetailModalOpen(false);
        }}
        onOpenComplete={handleOpenComplete}
        isDark={isDark}
        tagTickets={tagTickets}
        isLoadingTagTickets={isLoadingTagTickets}
        photosList={photosList}
        isLoadingPhotos={isLoadingPhotos}
        isUploadingPhoto={isUploadingPhoto}
        photoDescription={photoDescription}
        setPhotoDescription={setPhotoDescription}
        selectedPhotoPreview={selectedPhotoPreview}
        onDiscardPhotoPreview={handleDiscardPhotoPreview}
        onSavePhotoPreview={handleSavePhotoPreview}
        isUploadOpen={isUploadOpen}
        setIsUploadOpen={setIsUploadOpen}
        fileInputRef={fileInputRef}
        onFileSelect={handleFileSelect}
        onOpenLightbox={setLightboxPhoto}
        onOpenDeletePhoto={setPhotoToDelete}
      />

      {/* MODAL ELIMINAR */}
      <TagDeleteModal
        itemToDelete={itemToDelete}
        isDeleting={isDeleting}
        isDark={isDark}
        onClose={() => setItemToDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      {/* MODAL COMPLETAR INCIDENCIA */}
      <TagCompleteModal
        itemToComplete={itemToComplete}
        isCompleting={isCompleting}
        isDark={isDark}
        onClose={() => setItemToComplete(null)}
        onConfirm={handleConfirmComplete}
      />

      {/* MODALES DE FOTOS (LIGHTBOX Y ELIMINAR) */}
      <TagPhotoModals
        lightboxPhoto={lightboxPhoto}
        onCloseLightbox={() => setLightboxPhoto(null)}
        photoToDelete={photoToDelete}
        isDeletingPhoto={isDeletingPhoto}
        onCloseDeletePhoto={() => setPhotoToDelete(null)}
        onConfirmDeletePhoto={handleConfirmDeletePhoto}
        isDark={isDark}
      />
    </div>
  );
};

export default TagsSettingsSection;
